import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API, graphqlOperation } from "aws-amplify";
import {
  createBalanceSheetDashboardData,
  createPandLDashboardData,
  createTrialBalanceDashboardData,
} from "../../graphql/mutations";
import { PURGE } from "redux-persist";
import {
  listClosingStocks,
  listItemCategorymaster,
  listItemmasters,
} from "../../graphql/queries";

export const fetchBalanceSheetData = createAsyncThunk(
  "dashboard/fetchBalanceSheetData",
  async ({ StartDate, EndDate, OpeningBalance }, { rejectWithValue }) => {
    try {
      const response = await API.graphql(
        graphqlOperation(createBalanceSheetDashboardData, {
          StartDate,
          EndDate,
          OpeningBalance,
        })
      );
      return response?.data?.createBalanceSheetDashboardData[0];
    } catch (error) {
      console.error("Error fetching balance sheet data:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProfitNLossData = createAsyncThunk(
  "dashboard/fetchProfitNLossData",
  async ({ StartDate, EndDate, OpeningBalance }, { rejectWithValue }) => {
    try {
      const response = await API.graphql(
        graphqlOperation(createPandLDashboardData, {
          StartDate,
          EndDate,
          OpeningBalance,
        })
      );
      const res = await API.graphql(graphqlOperation(listClosingStocks));

      return {
        ProfitAndLoss:
          response?.data?.createPandLDashboardData[0].ProfitAndLoss,
        closingStock: res?.data?.listClosingStocks,
      };
    } catch (error) {
      console.error("Error fetching profit & Loss data:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTrialBalanceData = createAsyncThunk(
  "dashboard/fetchTrialBalanceData",
  async ({ StartDate, EndDate, OpeningBalance }, { rejectWithValue }) => {
    try {
      const response = await API.graphql(
        graphqlOperation(createTrialBalanceDashboardData, {
          StartDate,
          EndDate,
          OpeningBalance,
        })
      );
      return response?.data?.createTrialBalanceDashboardData[0];
    } catch (error) {
      console.error("Error fetching Trial balance data:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchItemMasterAndCategoryMaster = createAsyncThunk(
  "fetchItemMasterAndCategoryMaster",
  async () => {
    try {
      const resItemMaster = await API.graphql(
        graphqlOperation(listItemmasters)
      );
      const resGoods = await API.graphql(
        graphqlOperation(listItemCategorymaster, { MainCategory: "Goods" })
      );
      const resService = await API.graphql(
        graphqlOperation(listItemCategorymaster, { MainCategory: "Services" })
      );
      return {
        item: resItemMaster.data.listItemmasters || [],
        category: [
          ...resGoods.data.listItemCategorymaster,
          ...resService.data.listItemCategorymaster,
        ],
      };
    } catch (error) {
      throw error;
    }
  }
);

const initialTimeStampObj = {
  range: "day",
  startTimeStamp: null,
  endTimeStamp: null,
  fyId: null,
   basedOn:'invoiceDate',
};

const initialPandLTimeStampObj = {
  range: "year",
  startTimeStamp: null,
  endTimeStamp: null,
  fyId: null,
   basedOn:'invoiceDate',
};

const initialState = {
  profitNLoss: {
    render: true,
    closingStock: [],
    tableRowCount: {
      table1: 0,
      table2: 0,
      table3: 0,
      table4: 0,
    },
    isVertical: false,
    data: [],
  },
  balanceSheet: {
    render: true,
    data: {
      Balancesheet: [],
    },

    tableRowCount: {
      table1: 0,
      table2: 0,
    },
    isVertical: false,
    selectedParentId: "",
  },
  report: {
    dateRange: initialTimeStampObj,
  },
  pandlPeriod:initialPandLTimeStampObj,
  bsPeriod:initialTimeStampObj,
  tbPeriod:initialTimeStampObj,
  itemPeriod:initialTimeStampObj,
  trailBalance: {
    render: true,
    data: {
      Trialbalance: [],
    },
    searchParameters: {
      searchText: "",
      searchType: "all",
    },
  },
  item: {
    render: true,
    data: {
      itemMaster: [],
      categoryMaster: [],
    },
    searchParameters: {
      searchText: "",
      searchType: "all",
    },
  },
  OpStockItemWiseModal: false,
  loading: false,
  error: "",
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setRenderAllTrue: (state) => {
      Object.keys(state).forEach((key) => {
        if (typeof state[key] === "object") {
          state[key].render = true;
        }
      });
    },
    setLoading: (state, action) => {
      state.loading = action;
    },
    setRenderDashboard: (state, action) => {
      const { key } = action.payload;
      state[key].render = !state[key].render;
    },
    setSearchAction: (state, action) => {
      const { key, value } = action.payload;
      state.trailBalance.search[key] = value;
    },
    setDashboardSearchParameters: (state, action) => {
      const { outerKey, parameterToModify, newValue } = action.payload;
      state[outerKey].searchParameters[parameterToModify] = newValue;
    },
    setSelectedParentId: (state, action) => {
      const { outerKey, value } = action.payload;
      state[outerKey].selectedParentId = value;
    },
    setOPStockItemWiseModal: (state, action) => {
      state.OpStockItemWiseModal = !state.OpStockItemWiseModal;
    },
    setSwitchVertical: (state, action) => {
      const { outerKey, value } = action.payload;
      state[outerKey].isVertical = value;
    },
    setTableRowCount: (state, action) => {
      const { key, count } = action.payload;
      state.profitNLoss.tableRowCount[key] = count;
    },
    setTableRowCount2: (state, action) => {
      const { key, count } = action.payload;
      state.balanceSheet.tableRowCount[key] = count;
    },
    setDbDateRange: (state, action) => {
      const { outerKey, innerKey, value } = action.payload;
      if (state[outerKey]) {
        state[outerKey][innerKey] = value;
      }
    },
    setStartAndEndDate: (state, action) => {
      const { key1, key2, value1, value2 } = action.payload;
      state[key1] = value1;
      state[key2] = value2;
    },
    setResetPeriod: (state, action) => {
      const { outerKey } = action.payload;
      if (state[outerKey]) {
        // Special case for pandlPeriod - reset to yearly default
        if (outerKey === 'pandlPeriod') {
          state[outerKey] = initialPandLTimeStampObj;
        } else {
          state[outerKey] = initialTimeStampObj;
        }
      }
    },
    setResetDashboard: () => ({ ...initialState }),
    /** Reset all dashboard period slices when company or financial year changes. */
    setDashboardPeriodsForContext: (state, action) => {
      const {
        fyId,
        asOnPeriod,
        pandlPeriod,
      } = action.payload;
      if (asOnPeriod) {
        state.bsPeriod = { ...asOnPeriod };
        state.tbPeriod = { ...asOnPeriod };
        state.itemPeriod = { ...asOnPeriod };
      }
      if (pandlPeriod) {
        state.pandlPeriod = { ...pandlPeriod };
      }
      if (fyId != null && state.report?.dateRange) {
        state.report.dateRange.fyId = fyId;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBalanceSheetData.fulfilled, (state, action) => {
        state.balanceSheet.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchBalanceSheetData.pending, (state) => {
        state.loading = true;
        state.balanceSheet.render = false;
      })
      .addCase(fetchBalanceSheetData.rejected, (state) => {
        state.loading = false;
        state.error = "Something went wrong";
      })
      .addCase(fetchProfitNLossData.fulfilled, (state, action) => {
        const { ProfitAndLoss, closingStock } = action.payload;
        state.profitNLoss.data = ProfitAndLoss;
        state.profitNLoss.closingStock = closingStock;
        state.loading = false;
      })
      .addCase(fetchProfitNLossData.pending, (state) => {
        state.loading = true;
        state.profitNLoss.render = false;
      })
      .addCase(fetchProfitNLossData.rejected, (state) => {
        state.loading = false;
        state.error = "Something went wrong";
      })
      .addCase(fetchTrialBalanceData.fulfilled, (state, action) => {
        state.trailBalance.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchTrialBalanceData.pending, (state) => {
        state.loading = true;
        state.trailBalance.render = false;
      })
      .addCase(fetchTrialBalanceData.rejected, (state) => {
        state.loading = false;
        state.error = "Something went wrong";
      })
      .addCase(fetchItemMasterAndCategoryMaster.fulfilled, (state, action) => {
        console.log(action);
        state.item.data.itemMaster = action.payload.item;
        state.item.data.categoryMaster = action.payload.category;
        state.loading = false;
      })
      .addCase(fetchItemMasterAndCategoryMaster.pending, (state) => {
        state.loading = true;
        state.item.render = false;
      })
      .addCase(fetchItemMasterAndCategoryMaster.rejected, (state) => {
        state.loading = false;
        state.error = "Something went wrong";
      })
      .addCase(PURGE, () => initialState);
  },
});

export const {
  setResetPeriod,
  setRenderDashboard,
  setResetDashboard,
  setRenderAllTrue,
  setStartAndEndDate,
  setSearchAction,
  setDashboardSearchParameters,
  setSelectedParentId,
  setOPStockItemWiseModal,
  setSwitchVertical,
  setLoading,
  setTableRowCount,
  setTableRowCount2,
  setDbDateRange,
  setDashboardPeriodsForContext,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
